import { Button } from '@common/index';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './useDialog.module.scss';

interface DialogProps {
  title: string;
  subtitle?: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
  destructive?: boolean;
}

interface DialogState extends DialogProps {
  isOpen: boolean;
  resolve?: (confirmed: boolean) => void;
}

export const useDialog = () => {
    const [dialogState, setDialogState] = useState<DialogState>({
        title: '',
        isOpen: false,
    });

    const showDialog = useCallback((props: DialogProps): Promise<boolean> => {
        return new Promise((resolve) => {
            setDialogState({
                ...props,
                isOpen: true,
                resolve,
            });
        });
    }, []);

    const handleConfirm = useCallback(() => {
        const { onConfirm, resolve } = dialogState;
        onConfirm?.();
        resolve?.(true);
        setDialogState(prev => ({ ...prev, isOpen: false }));
    }, [dialogState]);

    const handleCancel = useCallback(() => {
        const { onCancel, resolve } = dialogState;
        onCancel?.();
        resolve?.(false);
        setDialogState(prev => ({ ...prev, isOpen: false }));
    }, [dialogState]);

    const handleOpenChange = useCallback((open: boolean) => {
        if (!open) {
            handleCancel();
        }
    }, [handleCancel]);

    const DialogPortal = useCallback(() => {
        if (!dialogState.isOpen) return null;

        return createPortal(
            <AlertDialog.Root open={dialogState.isOpen} onOpenChange={handleOpenChange}>
                <AlertDialog.Portal>
                    <AlertDialog.Overlay className={styles.overlay} />
                    <AlertDialog.Content className={styles.content}>
                        <div className={styles.header}>
                            <AlertDialog.Title className={styles.title}>
                                {dialogState.title}
                            </AlertDialog.Title>
                            {dialogState.subtitle ? (
                                <AlertDialog.Description className={styles.subtitle}>
                                    {dialogState.subtitle}
                                </AlertDialog.Description>
                            ) : null}
                        </div>
                        <div className={styles.actions}>
                            <AlertDialog.Cancel asChild>
                                <Button
                                    variant="outline"
                                    color="gray"
                                    size="2"
                                    onClick={handleCancel}
                                    style={{flex: 1}}
                                >
                                    {dialogState.cancelText || 'Cancel'}
                                </Button>
                            </AlertDialog.Cancel>
                            <AlertDialog.Action asChild>
                                <Button
                                    color={dialogState.destructive ? 'alert' : 'primary'}
                                    variant="solid"
                                    size="2"
                                    onClick={handleConfirm}
                                    style={{flex: 1}}
                                >
                                    {dialogState.confirmText || 'Confirm'}
                                </Button>
                            </AlertDialog.Action>
                        </div>
                    </AlertDialog.Content>
                </AlertDialog.Portal>
            </AlertDialog.Root>,
            document.body
        );
    }, [dialogState, handleConfirm, handleCancel, handleOpenChange]);

    return {
        showDialog,
        DialogPortal,
    };
};